import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import {
    clearOccActivity,
    clearOccProfiles,
    createOccProfile,
    loadOccActivity,
    loadOccProfiles
} from '../../../../store/participantService';
import { loadUsersByServiceIds } from '../../../../store/userService';
import { EDIT_ON_CLOSED_ROLES } from '../../../../utils/formValidation/commonConstants';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import AccordionHeader from '../../../ui/accordion/AccordionHeader';
import AccordionIcon from '../../../ui/accordion/AccordionIcon';

import OccProfile from './OccProfile';
import OccSummaryData from './OccSummaryData';

import local from './occStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const OccManagement = ({ currentParticipant }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [expanded, setExpanded] = useState('');
    const [isAddProfileDisabled, setIsAddProfileDisabled] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const occProfiles = useSelector((state) => state.entities.participantService.occProfiles);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USEEFFECTS
    useEffect(() => () => cleanUp(), []);

    useEffect(() => {
        if (!loggedInUser || !('serviceIds' in loggedInUser)) return;
        loggedInUser.serviceIds.length > 0 &&
            dispatch(loadUsersByServiceIds(loggedInUser.serviceIds));
    }, [loggedInUser.id]);

    useEffect(() => {
        dispatch(loadOccProfiles(currentParticipant.id));
        dispatch(loadOccActivity(currentParticipant.id));
    }, [currentParticipant]);

    useEffect(() => {
        if (!currentParticipant?.id) return;
        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' &&
            !hasRole(EDIT_ON_CLOSED_ROLES, loggedInUser?.roles);
        setIsDisabled(!hasRole(acceptedRoles, loggedInUser?.roles) || isDisabledOnClosedStatus);
    }, [currentParticipant.id]);

    useEffect(() => {
        if (occProfiles?.length && occProfiles[0].participantId !== currentParticipant.id) return;
        setIsAddProfileDisabled(
            occProfiles?.length ? occProfiles.some((el) => el.statusCode === 200) : false
        );
    }, [occProfiles]);

    useEffect(() => {
        if (successMessage === `OCC profile for ${currentParticipant.id} has been created`) {
            occProfiles?.length &&
                setIsAddProfileDisabled(occProfiles.some((el) => el.statusCode === 200));
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) setIsAddProfileDisabled(false);
    }, [errorMessage]);

    // FNs
    const cleanUp = () => {
        dispatch(clearOccActivity());
        dispatch(clearOccProfiles());
    };

    // EVENTHANDLERS
    const onAddProfile = () => {
        setIsAddProfileDisabled(true);
        const payload = {
            participantId: currentParticipant.id,
            userId: loggedInUser.id,
            theme: null
        };
        dispatch(createOccProfile(currentParticipant.id, payload));
    };

    const onPanelChange = (panel) => setExpanded(expanded === panel ? '' : panel);

    // RENDER
    return (
        <div className={local.occWrapper}>
            <Accordion
                expanded={expanded === 'occ'}
                onChange={() => onPanelChange('occ')}
                slotProps={{ transition: { mountOnEnter: true } }}
                disabled={!hasRole(acceptedRoles, loggedInUser?.roles)}>
                <AccordionSummary
                    expandIcon={<AccordionIcon />}
                    aria-controls={`panel1a-content`}
                    id={`panel1a-header`}>
                    <AccordionHeader>Online Career Centre</AccordionHeader>
                </AccordionSummary>
                <AccordionDetails>
                    { expanded === 'occ' && (
                        <>
                            <div className={local.occActions}>
                                <Button
                                    id="createOCCProfileButton"
                                    type="text"
                                    onClick={onAddProfile}
                                    disabled={isAddProfileDisabled || isDisabled}
                                    content="Create OCC Profile"
                                />
                            </div>
                            <OccProfile currentParticipant={currentParticipant} />
                            <OccSummaryData currentParticipant={currentParticipant} />
                        </>
                    )}
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

OccManagement.propTypes = {
    currentParticipant: PropTypes.object
};

export default OccManagement;
